import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, ScrollView, TextInput as RNTextInput} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Button,
  Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {priceBookApi} from '../../services/api/priceBookApi';
import {measurementApi} from '../../services/api/measurementApi';
import {colors, spacing} from '../../theme';

const PriceBookPricesScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {priceBook} = route.params;

  const [garments, setGarments] = useState([]);
  const [services, setServices] = useState([]);
  const [existingItems, setExistingItems] = useState([]);
  const [prices, setPrices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const buildKey = (garmentId, serviceId) => `${garmentId}_${serviceId}`;

  const fetchData = useCallback(async () => {
    try {
      const [garmentRes, serviceRes, itemsRes] = await Promise.all([
        measurementApi.listCatalogItems(false, 'GARMENT'),
        measurementApi.listCatalogItems(false, 'SERVICE'),
        priceBookApi.listItems(priceBook.id),
      ]);

      const garmentList = garmentRes.data.data || [];
      const serviceList = serviceRes.data.data || [];
      const items = itemsRes.data.data || [];

      setGarments(garmentList);
      setServices(serviceList);
      setExistingItems(items);

      // Build price map from existing items
      const priceMap = {};
      items.forEach(item => {
        if (item.serviceCatalogItemId) {
          const key = buildKey(item.catalogItemId, item.serviceCatalogItemId);
          priceMap[key] = {
            value: String(item.unitPrice),
            itemId: item.id,
          };
        }
      });
      setPrices(priceMap);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [priceBook.id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePriceChange = useCallback((garmentId, serviceId, value) => {
    const key = buildKey(garmentId, serviceId);
    setPrices(prev => ({
      ...prev,
      [key]: {...(prev[key] || {}), value},
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Build a set of existing item keys for quick lookup
      const existingMap = {};
      existingItems.forEach(item => {
        if (item.serviceCatalogItemId) {
          const key = buildKey(item.catalogItemId, item.serviceCatalogItemId);
          existingMap[key] = item;
        }
      });

      const promises = [];

      for (const garment of garments) {
        for (const service of services) {
          const key = buildKey(garment.id, service.id);
          const entry = prices[key];
          const existing = existingMap[key];
          const newPrice = entry?.value?.trim();

          if (newPrice && !isNaN(parseFloat(newPrice)) && parseFloat(newPrice) >= 0) {
            if (existing) {
              // Update existing item if price changed
              if (String(existing.unitPrice) !== newPrice) {
                promises.push(
                  priceBookApi.updateItem(priceBook.id, existing.id, {
                    unitPrice: parseFloat(newPrice),
                  }),
                );
              }
            } else {
              // Create new item
              promises.push(
                priceBookApi.addItem(priceBook.id, {
                  catalogItemId: garment.id,
                  serviceCatalogItemId: service.id,
                  unitPrice: parseFloat(newPrice),
                }),
              );
            }
          } else if ((!newPrice || newPrice === '') && existing) {
            // Remove item if price cleared
            promises.push(
              priceBookApi.removeItem(priceBook.id, existing.id),
            );
          }
        }
      }

      await Promise.all(promises);
      setSnackbar('Prices saved successfully');
      // Refresh data to get updated item IDs
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  }, [garments, services, prices, existingItems, priceBook.id, fetchData, t]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const COL_WIDTH = 100;
  const LABEL_WIDTH = 120;

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={priceBook.name}
          subtitle="Edit Prices"
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      {garments.length === 0 || services.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>
            {garments.length === 0
              ? 'No garment catalog items found.'
              : 'No service catalog items found.'}
          </Text>
        </View>
      ) : (
        <>
          <ScrollView style={styles.scrollContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator>
              <View>
                {/* Header row: empty corner + service names */}
                <View style={styles.headerRow}>
                  <View style={[styles.cornerCell, {width: LABEL_WIDTH}]}>
                    <Text style={styles.cornerText}>Garment ↓ / Service →</Text>
                  </View>
                  {services.map(service => (
                    <View
                      key={service.id}
                      style={[styles.headerCell, {width: COL_WIDTH}]}>
                      <Text style={styles.headerText} numberOfLines={2}>
                        {service.name}
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Data rows: garment name + price inputs */}
                {garments.map(garment => (
                  <View key={garment.id} style={styles.dataRow}>
                    <View style={[styles.labelCell, {width: LABEL_WIDTH}]}>
                      <Text style={styles.labelText} numberOfLines={2}>
                        {garment.name}
                      </Text>
                    </View>
                    {services.map(service => {
                      const key = buildKey(garment.id, service.id);
                      const entry = prices[key];
                      return (
                        <View
                          key={service.id}
                          style={[styles.priceCell, {width: COL_WIDTH}]}>
                          <RNTextInput
                            style={styles.priceInput}
                            value={entry?.value || ''}
                            onChangeText={val =>
                              handlePriceChange(garment.id, service.id, val)
                            }
                            keyboardType="numeric"
                            placeholder="—"
                            placeholderTextColor={colors.border}
                          />
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>
            </ScrollView>
          </ScrollView>

          <View style={styles.footer}>
            <Button
              mode="contained"
              onPress={handleSave}
              loading={isSaving}
              disabled={isSaving}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </View>
        </>
      )}

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg},
  emptyText: {color: colors.textSecondary, textAlign: 'center'},
  scrollContainer: {flex: 1},
  headerRow: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
  },
  cornerCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  cornerText: {
    fontSize: 11,
    color: colors.white,
    fontWeight: '600',
  },
  headerCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  headerText: {
    fontSize: 12,
    color: colors.white,
    fontWeight: '600',
    textAlign: 'center',
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: colors.border,
  },
  labelCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    backgroundColor: colors.surface,
    borderRightWidth: 1,
    borderColor: colors.border,
  },
  labelText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.text,
  },
  priceCell: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRightWidth: 1,
    borderColor: colors.border,
  },
  priceInput: {
    width: '90%',
    height: 36,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingHorizontal: 8,
    fontSize: 14,
    color: colors.text,
    textAlign: 'center',
    backgroundColor: colors.background,
  },
  footer: {
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderColor: colors.border,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
  },
});

export default PriceBookPricesScreen;
